import React, { useLayoutEffect, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion, type Variants, type Transition } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  getScannerPrefs,
  upsertScannerPrefs,
  type PrefsOut,
} from '../api/endpoints';
import {
  postScannerStart,
  postScannerPrefsView,
  postScannerPrefsUpdate,
} from '../api/telemetry';
import useScanCredits from '../hooks/useScanCredits';

import { IconGear, IconBarcode } from '../components/icons/AnalyzerIcons';
import ModalShell from '../components/ModalShell';
import ProductSideFlow from './shared/productSideFlow/ProductSideFlow';
import type { SideProduct } from './shared/productSideFlow/reducer';
import type { ScreenProps } from '../types/screen';
import { getErrorMessage } from '../utils/errors';

export interface ProductSoloInput {
  /** Optional pre-filled barcode (when arriving from a tool call). */
  barcode?: string;
}

export interface ProductSoloResult {
  product_id: number;
  barcode: string;
  source?: string;
}

/* ── Motion constants ───────────────────────────────── */
const spring: Transition = { type: 'spring', stiffness: 420, damping: 36, mass: 0.9 };
const quick: Transition = { duration: 0.18 };
const pageVariants: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08, delayChildren: 0.04 } },
};
const sectionItem: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: spring },
};

/* ── Micro-UX helpers ───────────────────────────────── */
function AnimatedSwap({
  value,
  className,
  as: Tag = 'span',
}: { value: React.ReactNode; className?: string; as?: React.ElementType }) {
  return (
    <AnimatePresence mode="wait">
      <motion.span
        key={typeof value === 'string' || typeof value === 'number' ? String(value) : 'swap'}
        initial={{ opacity: 0, y: -4 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 4 }}
        transition={{ duration: 0.18 }}
        className={className}
      >
        <Tag>{value}</Tag>
      </motion.span>
    </AnimatePresence>
  );
}

export function FlashHalo({
  children,
  depsKey,
  className,
  color = '42,164,166',
  intensity = 0.28,
  duration = 0.8,
}: {
  children: React.ReactNode;
  depsKey: React.Key;
  className?: string;
  color?: string;
  intensity?: number;
  duration?: number;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [radius, setRadius] = useState<string>('12px');
  const [display, setDisplay] = useState<'block' | 'inline-block'>('inline-block');

  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const child = (wrap.firstElementChild ?? wrap) as HTMLElement;
    const d = getComputedStyle(child).display;
    setDisplay(d === 'inline' || d === 'inline-block' || child.tagName === 'SPAN' ? 'inline-block' : 'block');
    const br = getComputedStyle(child).borderRadius;
    if (br && br !== '0px') {
      setRadius(br);
    } else {
      const rect = child.getBoundingClientRect();
      const pillLikely = rect.width > rect.height * 1.8;
      setRadius(pillLikely ? '9999px' : '12px');
    }
  }, [children]);

  return (
    <div ref={wrapRef} className={`relative isolate ${className ?? ''}`} style={{ display }}>
      <AnimatePresence initial={false} mode="wait">
        <motion.div
          key={String(depsKey)}
          initial={{ opacity: 0.9, scale: 0.985 }}
          animate={{ opacity: 0, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration, ease: 'easeOut' }}
          className="pointer-events-none absolute -inset-1 will-change-[opacity,transform]"
          aria-hidden
          style={{
            borderRadius: radius,
            background: `radial-gradient(120% 120% at 50% 50%, rgba(${color}, ${intensity}) 0%, rgba(${color}, 0) 60%)`,
            filter: 'blur(12px)',
          }}
        />
      </AnimatePresence>
      {children}
    </div>
  );
}

const ALLERGENS = [
  'nickel','benzoic_acid','methylisothiazolinone','methylchloroisothiazolinone','parabens','lanolin',
  'fragrance','linalool','limonene','citral','hydroxycitronellal','balsam_peru','tocopherol_allergy',
] as const;
type Allergen = typeof ALLERGENS[number];

const SENS_OPTIONS = [
  { v: 'none', label: 'None' },
  { v: 'possible', label: 'Possible' },
  { v: 'likely', label: 'Likely' },
] as const;

const ETHICAL_OPTIONS = [
  { v: 'off', label: 'Off' },
  { v: 'vegan', label: 'Vegan' },
  { v: 'halal', label: 'Halal' },
  { v: 'cruelty_free', label: 'Cruelty-free' },
] as const;

const labelize = (k: string) => k.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());

function Toggle({
  label, hint, checked, onChange,
}: { label: string; hint?: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="card card--compact card--bordered flex items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="t-small" style={{ fontWeight: 500, color: 'var(--ink)' }}>{label}</div>
        {hint && <div className="t-small t-mute">{hint}</div>}
      </div>
      <button
        type="button"
        onClick={() => { onChange(!checked); }}
        className="relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200"
        style={{ background: checked ? 'var(--c-kivo-teal)' : 'var(--c-charcoal-12)' }}
        aria-pressed={checked}
      >
        <span
          className="pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transition duration-200"
          style={{ transform: checked ? 'translateX(20px)' : 'translateX(0)' }}
        />
      </button>
    </div>
  );
}

function PreferencesPage({ onBack }: { onBack: () => void }) {
  const prefersReduced = useReducedMotion() ?? false;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [allergies, setAllergies] = useState<Allergen[]>([]);
  const [sensitivity, setSensitivity] = useState<'none' | 'possible' | 'likely'>('likely');
  const [acneProne, setAcneProne] = useState(false);
  const [ethical, setEthical] = useState<'off' | 'vegan' | 'halal' | 'cruelty_free'>('off');
  const [derm, setDerm] = useState(false);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      setErr(null);
      try {
        const p: PrefsOut = await getScannerPrefs();
        const hasTracked = sessionStorage.getItem('telemetry_scanner_prefs_viewed');
        if (!hasTracked) {
          void postScannerPrefsView().catch(function ignoreError() { /* non-fatal */ });
          sessionStorage.setItem('telemetry_scanner_prefs_viewed', '1');
        }
        setAllergies(p.allergies as Allergen[]);
        setSensitivity(p.sensitivity);
        setAcneProne(p.acne_prone);
        setEthical(p.ethical_mode);
        setDerm(p.cautious_actives);
      } catch (error: unknown) {
        setErr(getErrorMessage(error, 'Failed to load preferences'));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggleAllergen = (a: Allergen) =>
    { setAllergies((prev) => (prev.includes(a) ? prev.filter((x) => x !== a) : [...prev, a])); };

  const save = async () => {
    setSaving(true);
    setErr(null);
    try {
      await upsertScannerPrefs({
        allergies,
        sensitivity,
        acne_prone: acneProne,
        ethical_mode: ethical,
        cautious_actives: derm,
      });
      void postScannerPrefsUpdate([]).catch(function ignoreError() { /* non-fatal */ });
      onBack();
    } catch (error: unknown) {
      setErr(getErrorMessage(error, 'Failed to save'));
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-2xl py-4 sm:py-6">
      <div
        className="sticky top-0 z-10 px-4 sm:px-0 py-3"
        style={{ background: 'var(--c-cream-weak)', borderBottom: '1px solid var(--rule)', backdropFilter: 'blur(8px)' }}
      >
        <div className="flex items-center justify-between">
          <h1 className="t-h3">Scanner preferences</h1>
          <div className="row-3">
            <motion.button
              onClick={onBack}
              whileTap={{ scale: prefersReduced ? 1 : 0.97 }}
              className="btn btn--ghost btn--sm"
            >
              Skip
            </motion.button>
            <motion.button
              onClick={save}
              disabled={saving}
              whileTap={{ scale: prefersReduced ? 1 : 0.97 }}
              className="btn btn--primary btn--sm"
            >
              <AnimatedSwap value={saving ? 'Saving…' : 'Save'} />
            </motion.button>
          </div>
        </div>
      </div>

      <div className="px-4 sm:px-0 pt-4">
        <div className="card card--sand card--compact">
          <span className="t-small" style={{ fontWeight: 500 }}>Optional.</span>
          <span className="t-small t-mute"> Personalize your preference scoring — or skip and set them any time.</span>
        </div>
      </div>

      <div className="px-4 sm:px-0 py-4 stack-5">
        {loading ? (
          <p className="t-small t-mute">Loading…</p>
        ) : (
          <>
            <section className="stack-3">
              <div className="t-small" style={{ fontWeight: 500, color: 'var(--ink)' }}>Ingredients to avoid</div>
              <p className="t-small t-mute">Select any known allergens to flag risky products.</p>
              <div className="flex flex-wrap gap-2">
                {ALLERGENS.map((a) => {
                  const active = allergies.includes(a);
                  return (
                    <motion.button
                      key={a}
                      onClick={() => { toggleAllergen(a); }}
                      whileTap={{ scale: prefersReduced ? 1 : 0.96 }}
                      className={active ? 'pill pill--teal' : 'pill'}
                      style={{ cursor: 'pointer', transition: 'background var(--t-fast) var(--ease)' }}
                    >
                      {active && <span className="dot" />}
                      <AnimatedSwap value={labelize(a)} />
                    </motion.button>
                  );
                })}
              </div>
            </section>

            <section className="stack-3">
              <div className="t-small" style={{ fontWeight: 500, color: 'var(--ink)' }}>Skin sensitivity</div>
              <p className="t-small t-mute">How strict should we be about potential irritants?</p>
              <div
                className="grid grid-cols-3 w-full overflow-hidden"
                style={{ borderRadius: 'var(--r-sm)', border: '1px solid var(--rule)' }}
              >
                {SENS_OPTIONS.map((o, i) => (
                  <button
                    key={o.v}
                    onClick={() => { setSensitivity(o.v); }}
                    className="px-3 py-2 t-small text-center"
                    style={{
                      borderLeft: i > 0 ? '1px solid var(--rule)' : undefined,
                      background: sensitivity === o.v ? 'var(--c-kivo-teal)' : 'var(--c-cream)',
                      color: sensitivity === o.v ? 'var(--c-cream)' : 'var(--ink)',
                      transition: 'background var(--t-fast) var(--ease)',
                    }}
                  >
                    <AnimatedSwap value={o.label} />
                  </button>
                ))}
              </div>
            </section>

            <section className="stack-3">
              <div className="t-small" style={{ fontWeight: 500, color: 'var(--ink)' }}>Ethical preference</div>
              <p className="t-small t-mute">Pick one.</p>
              <div className="flex flex-wrap gap-2">
                {ETHICAL_OPTIONS.map((o) => {
                  const active = ethical === o.v;
                  return (
                    <motion.button
                      key={o.v}
                      onClick={() => { setEthical(o.v); }}
                      whileTap={{ scale: prefersReduced ? 1 : 0.96 }}
                      className={active ? 'pill pill--teal' : 'pill'}
                      style={{ cursor: 'pointer', transition: 'background var(--t-fast) var(--ease)' }}
                    >
                      {active && <span className="dot" />}
                      <AnimatedSwap value={o.label} />
                    </motion.button>
                  );
                })}
              </div>
            </section>

            <section className="grid sm:grid-cols-3 gap-3">
              <Toggle label="Breakout-prone" hint="Prefer non-comedogenic picks." checked={acneProne} onChange={setAcneProne} />
              <Toggle label="Prefer gentle formulas" hint="Go easy on strong actives." checked={derm} onChange={setDerm} />
            </section>

            <AnimatePresence>
              {err && (
                <motion.div
                  key={err}
                  initial={{ y: -6, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: -6, opacity: 0 }}
                  transition={quick}
                  className="t-small"
                  style={{ color: 'var(--c-error)', background: 'rgba(196,103,58,0.08)', border: '1px solid rgba(196,103,58,0.2)', borderRadius: 'var(--r-sm)', padding: '8px 12px' }}
                >
                  {err}
                </motion.div>
              )}
            </AnimatePresence>
          </>
        )}
      </div>
    </div>
  );
}

const ProductSoloScreen: React.FC<ScreenProps<ProductSoloInput, ProductSoloResult>> = ({
  mode,
  input,
  onComplete,
  onCancel,
}) => {
  const nav = useNavigate();
  const prefersReduced = useReducedMotion() ?? false;
  const [showPrefs, setShowPrefs] = useState(false);
  const [product, setProduct] = useState<SideProduct | null>(null);

  // Credit gating (cost comes from the API via the user context — never hardcoded).
  const { total: creditsLeft, outOfQuota } = useScanCredits('product_barcode');
  const scansLeft = creditsLeft;

  useEffect(() => {
    if (mode !== 'page') return;
    void (async () => {
      try {
        const p: PrefsOut = await getScannerPrefs();
        const looksDefault =
          p.allergies.length === 0 &&
          p.sensitivity === 'likely' &&
          !p.acne_prone &&
          p.ethical_mode === 'off' &&
          !p.cautious_actives;
        const shown = localStorage.getItem('prefsPagePromptShown');
        if (looksDefault && !shown) {
          localStorage.setItem('prefsPagePromptShown', '1');
          setShowPrefs(true);
        }
      } catch { /* ignore */ }
    })();
  }, [mode]);

  // A product is ready (picked, verified hit, or read from its label).
  const onProduct = useCallback((next: SideProduct | null) => {
    setProduct(next);
    if (next) void postScannerStart().catch(function ignoreError() { /* non-fatal */ });
  }, []);

  // The result page loads by id: '' marks "no barcode needed", and the
  // source only labels the page (a verified product is a catalog one).
  const openAnalysis = (p: SideProduct) => {
    const result: ProductSoloResult = { product_id: p.productId, barcode: '', source: p.verified ? 'catalog' : 'ai' };
    if (mode === 'modal') {
      onComplete?.(result);
      return;
    }
    nav(`/scanner/result/${p.productId}`, { state: { barcode: result.barcode, source: result.source } });
  };

  const hero = useMemo(
    () => (
      <div className="card card--bordered relative overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'linear-gradient(135deg, var(--c-teal-weak) 0%, transparent 65%)' }}
        />
        <div className="relative p-4 sm:p-6 flex flex-col gap-3 sm:flex-row sm:items-center">
          <div
            className="grid place-items-center w-9 h-9 rounded-full shrink-0"
            style={{ background: 'var(--c-teal-weak)', color: 'var(--c-kivo-teal)' }}
          >
            <IconBarcode />
          </div>
          <div className="min-w-0">
            <h1 className="t-h3">Scan a Product</h1>
            <p className="t-small t-mute">
              Scan the barcode, type it, or take a photo of the product. We&apos;ll find it and analyse its ingredients.
            </p>
            {mode === 'page' && (
              <button
                type="button"
                onClick={() => { nav('/scanner/how-it-works'); }}
                className="mt-2 inline-flex items-center gap-1 t-small"
                style={{ color: 'var(--c-kivo-teal)', textDecoration: 'underline' }}
              >
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 18h.01" />
                  <path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2Zm0 6a2 2 0 0 1 2 2c0 1.5-2 2-2 3" />
                </svg>
                Find more about how this works
              </button>
            )}
          </div>

          <div className="mt-2 sm:mt-0 sm:ml-auto flex items-center gap-2">
            {scansLeft !== null && (
              <FlashHalo depsKey={scansLeft}>
                <span
                  className={outOfQuota ? 'pill' : 'pill pill--teal'}
                  style={outOfQuota ? { color: 'var(--c-error)' } : undefined}
                  title={outOfQuota ? 'Out of credits' : `${scansLeft} credits left`}
                >
                  {outOfQuota
                    ? <><span className="dot dot--coral" /><AnimatedSwap value="0 credits" /></>
                    : <><span className="dot" /><AnimatedSwap value={`${scansLeft} credits`} /></>
                  }
                </span>
              </FlashHalo>
            )}
            <button
              onClick={() => { setShowPrefs(true); }}
              className="btn btn--ghost btn--sm"
            >
              <span className="sm:hidden"><IconGear /></span>
              <span className="hidden sm:inline-flex items-center gap-1"><IconGear /> Preferences</span>
            </button>
          </div>
        </div>
      </div>
    ),
    [outOfQuota, scansLeft, nav, mode],
  );

  const mainView = (
    <motion.div
      key="main"
      className="stack-5"
      variants={pageVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.div variants={sectionItem}>
        {hero}
      </motion.div>

      {outOfQuota && (
        <motion.div variants={sectionItem}>
          <div className="card card--sand card--compact flex flex-wrap items-center justify-between gap-2" role="status">
            <p className="t-small" style={{ margin: 0 }}>
              You&apos;re out of credits. You can still open products from your favorites; top up to scan new ones.
            </p>
            <button type="button" onClick={() => { nav('/plans'); }} className="btn btn--primary btn--sm">Top up</button>
          </div>
        </motion.div>
      )}

      <motion.div variants={sectionItem}>
        <ProductSideFlow
          side="a"
          context="solo"
          label="Your product"
          initialBarcode={input?.barcode}
          onChange={onProduct}
          footer={product ? (
            <motion.button
              type="button"
              onClick={() => { openAnalysis(product); }}
              whileTap={{ scale: prefersReduced ? 1 : 0.97 }}
              className="btn btn--primary btn--full"
            >
              See full analysis
            </motion.button>
          ) : null}
        />
      </motion.div>
    </motion.div>
  );

  const inner = (
    <AnimatePresence mode="wait" initial={false}>
      {showPrefs ? (
        <motion.div
          key="prefs"
          initial={{ opacity: 0, x: prefersReduced ? 0 : 32 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: prefersReduced ? 0 : 32 }}
          transition={spring}
        >
          <PreferencesPage onBack={() => { setShowPrefs(false); }} />
        </motion.div>
      ) : (
        mainView
      )}
    </AnimatePresence>
  );

  if (mode === 'modal') {
    return (
      <ModalShell
        title="Scan a product"
        onClose={() => onCancel?.()}
        widthClass="max-w-4xl"
      >
        <div className="p-4 sm:p-5">{inner}</div>
      </ModalShell>
    );
  }

  return inner;
};

export default ProductSoloScreen;
